import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from 'react';
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import { ArrowLeft, Search, MessageCircle, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { Button } from '@edusense/shared-ui-kit';
import { useChat } from '@edusense/ai-chat/contexts/ChatContext';
import { CHAT_TEST_IDS } from '@edusense/shared-constants';
import { SearchBar, ChatList, ChatItem, EmptyState } from './ChatHistoryModal.styles';
import styled from '@emotion/styled';
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--theme-paper, #ffffff);
  
  .dark & {
    background-color: var(--theme-paper, #1f2937);
  }
`;
const Header = styled.header`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--theme-border, #e5e7eb);
  background-color: var(--theme-paper, #ffffff);
  flex-shrink: 0;
  
  .dark & {
    background-color: var(--theme-paper, #1f2937);
    border-color: var(--theme-border, #374151);
  }
`;
const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const Title = styled.h1`
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: var(--theme-text, #111827);
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .dark & {
    color: var(--theme-text, #f3f4f6);
  }
`;
const ChatHistoryView = ({ onBack })=>{
    const { getAllChats, selectChat, deleteChat } = useChat();
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredChats, setFilteredChats] = useState([]);
    const searchInputRef = useRef(null);
    // 모든 채팅 기록 가져오기
    useEffect(()=>{
        const allChats = getAllChats();
        setFilteredChats(allChats);
        // 뷰가 열리면 검색 입력에 포커스
        setTimeout(()=>{
            searchInputRef.current?.focus();
        }, 100);
    }, [
        getAllChats
    ]);
    // 검색 필터링
    useEffect(()=>{
        if (!searchQuery.trim()) {
            setFilteredChats(getAllChats());
            return;
        }
        const query = searchQuery.toLowerCase();
        const filtered = getAllChats().filter((chat)=>chat.title.toLowerCase().includes(query) || (chat.lastMessagePreview?.toLowerCase().includes(query) ?? false) || chat.messages.some((msg)=>msg.content.toLowerCase().includes(query)));
        setFilteredChats(filtered);
    }, [
        searchQuery,
        getAllChats
    ]);
    // 날짜 포맷팅
    const formatDate = (date)=>{
        const now = new Date();
        const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
        if (diffInHours < 24) {
            return format(date, 'HH:mm', {
                locale: ko
            });
        } else if (diffInHours < 48) {
            return '어제';
        } else if (diffInHours < 168) {
            return format(date, 'EEEE', {
                locale: ko
            });
        } else {
            return format(date, 'M월 d일', {
                locale: ko
            });
        }
    };
    // 채팅 선택 핸들러
    const handleChatSelect = (chatId)=>{
        selectChat(chatId);
        onBack();
    };
    const handleDeleteChat = async (event, chatId)=>{
        event.stopPropagation();
        const confirmDelete = window.confirm('선택한 대화 기록을 삭제할까요? 삭제 후에는 복구할 수 없습니다.');
        if (!confirmDelete) {
            return;
        }
        try {
            await deleteChat(chatId);
            setFilteredChats((prev)=>prev.filter((chat)=>chat.id !== chatId));
        } catch (error) {
            console.error('Failed to delete chat history:', error);
        }
    };
    return /*#__PURE__*/ _jsxs(Container, {
        children: [
            /*#__PURE__*/ _jsx(Header, {
                children: /*#__PURE__*/ _jsxs(TitleRow, {
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            iconOnly: true,
                            icon: /*#__PURE__*/ _jsx(ArrowLeft, {
                                size: 18
                            }),
                            variant: "ghost",
                            size: "sm",
                            onClick: onBack,
                            label: "뒤로가기"
                        }),
                        /*#__PURE__*/ _jsx(Title, {
                            style: {
                                flex: 1,
                                marginLeft: '0.75rem'
                            },
                            children: "채팅 기록"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    padding: '1rem',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column'
                },
                children: [
                    /*#__PURE__*/ _jsxs(SearchBar, {
                        children: [
                            /*#__PURE__*/ _jsx(Search, {
                                size: 20
                            }),
                            /*#__PURE__*/ _jsx("input", {
                                ref: searchInputRef,
                                type: "text",
                                placeholder: "채팅 내용 검색...",
                                value: searchQuery,
                                onChange: (e)=>setSearchQuery(e.target.value)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(ChatList, {
                        children: filteredChats.length === 0 ? /*#__PURE__*/ _jsxs(EmptyState, {
                            children: [
                                /*#__PURE__*/ _jsx(MessageCircle, {
                                    size: 48
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    children: searchQuery ? '검색 결과가 없습니다' : '채팅 기록이 없습니다'
                                })
                            ]
                        }) : filteredChats.map((chat)=>/*#__PURE__*/ _jsxs(ChatItem, {
                                onClick: ()=>handleChatSelect(chat.id),
                                whileHover: {
                                    scale: 1.02
                                },
                                whileTap: {
                                    scale: 0.98
                                },
                                "data-testid": CHAT_TEST_IDS.HISTORY_ITEM,
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "chat-info",
                                        children: [
                                            /*#__PURE__*/ _jsx("h3", {
                                                children: chat.title
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "last-message",
                                                children: chat.lastMessagePreview || chat.messages[chat.messages.length - 1]?.content || '메시지가 없습니다'
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "chat-meta",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "date",
                                                children: formatDate(chat.lastMessageAt ?? chat.updated_at)
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                className: "message-count",
                                                children: [
                                                    chat.messageCount ?? chat.messages.length,
                                                    "개"
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(Button, {
                                                iconOnly: true,
                                                size: "sm",
                                                variant: "ghost",
                                                icon: /*#__PURE__*/ _jsx(Trash2, {
                                                    className: "w-4 h-4"
                                                }),
                                                onClick: (event)=>handleDeleteChat(event, chat.id),
                                                "aria-label": "대화 삭제"
                                            })
                                        ]
                                    })
                                ]
                            }, chat.id))
                    })
                ]
            })
        ]
    });
};
export default ChatHistoryView;
